(ns ui.gallery.actions
  "The action handlers of the Replicant gallery (`:gallery/*`). They
   are the imperative shell around the pure functions of
   `ui.gallery.layout`, `ui.gallery.selection` and `ui.gallery.drag`:
   they read the browser event, update the `:gallery` entry of the
   store and talk to the server."
  (:require [app.upload-manager.core :as upload-manager]
            [app.util.transit :as transit]
            [cljs.reader :as edn]
            [parts.replicant.query :as query]
            [parts.replicant.router :as router]
            [storrito.upload.story-upload :as story-upload]
            [ui.app-frame :as app-frame]
            [ui.error-alert :as error-alert]
            [ui.gallery.drag :as drag]
            [ui.gallery.folder-delete :as folder-delete]
            [ui.gallery.grid :as grid]
            [ui.gallery.layout :as layout]
            [ui.gallery.move :as move]
            [ui.gallery.prompt-modal :as prompt-modal]
            [ui.gallery.query :as gq]
            [ui.gallery.selection :as selection]
            [ui.upload-status :as upload-status]))

(defn fetch-query!
  "POSTs the query `q` to the `/query` endpoint and returns a promise of
   the decoded response, without touching the query log (for one-off
   queries whose params change with every call, so the log does not
   fill up with them)."
  [{:keys [transit/read-opts transit/write-opts]} q]
  (-> (js/fetch "/query"
                #js {:method "POST"
                     :body (transit/transit-encode q write-opts)})
      (.then (fn [response]
               (.text response)))
      (.then (fn [text]
               (transit/transit-decode text read-opts)))))

(defn show-error!
  [store message]
  (error-alert/show-error {:error message
                           :store store}))

(defn scroll-top!
  "Scrolls the grid to the top and resets the stored offsets right away,
   since the scroll event only fires later (or not at all when nothing
   was scrolled) and the next render must not use offsets of a page
   that the new folder or tab may not have."
  [store]
  (let [node (js/document.getElementById grid/grid-id)]
    (when node
      (set! (.-scrollTop node) 0))
    (swap! store
           assoc-in
           [:gallery :scroll]
           (if node
             (grid/node-offsets node)
             (grid/initial-offsets)))))

(defn gallery-href
  [folder-uuid]
  (str "/ui/gallery"
       (when folder-uuid
         (str "?folder=" folder-uuid))))

(defn navigate!
  "Navigates the Replicant router to `href` (a `/ui/gallery` URL), which
   runs the `:on-load` of the page again with the new folder. Uses
   `pushState`, so browser back / forward walk through the folders, or
   `replaceState` when `replace?` is set (a missing folder must not stay
   in the history)."
  [{:keys [ui/store] :as w} href replace?]
  (if replace?
    (.replaceState js/history nil "" href)
    (.pushState js/history nil "" href))
  (router/navigate! w)
  (swap! store assoc :ui/active-url js/location.pathname))

(defn folder-missing!
  [{:keys [ui/store] :as w}]
  (show-error! store "This folder no longer exists.")
  (navigate! w "/ui/gallery" true))

(defn latest-gallery-result
  "The most recently requested gallery result in the query log,
   regardless of its params. A `ui.notify` refresh that arrives after
   `load-gallery!` pruned its query puts that query back into the log
   with a response only, without a request time. Such a stale entry is
   skipped."
  [state]
  (->> (:ui.query/log state)
       (keys)
       (filter gq/gallery-query?)
       (keep (fn [q]
               (let [result (query/get-result state q)
                     requested-at (query/requested-at state q)]
                 (when (and result requested-at)
                   [requested-at result]))))
       (sort-by (comp - (fn [[t _]] (.getTime t))))
       (first)
       (second)))

(defn load-gallery!
  "Loads the `:query/gallery` for the current gallery state. The log
   keeps only this one gallery query (see `gq/prune-log`), the result of
   the previous one moves into `:last-result`, so the grid keeps
   rendering it until the new page arrived. A response for a query that
   is no longer the current one (the user scrolled on) is dropped."
  [{:keys [ui/store] :as w}]
  (let [q (gq/gallery-query @store)]
    (swap! store
           (fn [state]
             (-> state
                 (assoc-in [:gallery :last-result]
                           (or (latest-gallery-result state)
                               (get-in state [:gallery :last-result])))
                 (update :ui.query/log gq/prune-log gq/gallery-query? q)
                 (query/send-request (js/Date.) q))))
    (-> (fetch-query! w q)
        (.then (fn [response]
                 (when (= q (gq/gallery-query @store))
                   (swap! store query/receive-response (js/Date.) q response)
                   (when (get-in response [:result :gallery/folder-missing?])
                     (folder-missing! w)))))
        (.catch (fn [error]
                  (js/console.error "gallery query failed:" error)
                  (when (= q (gq/gallery-query @store))
                    (swap! store query/receive-response (js/Date.) q
                           {:error (.-message error)})
                    ;; without a previous result the grid shows the
                    ;; error hint itself:
                    (when (gq/result @store)
                      (show-error! store "The gallery could not be refreshed. Please try again."))))))))

(defn init!
  "The `:on-load` of the page: seeds the folder from the `?folder=<uuid>`
   query param (nil for the top-level gallery) and loads the gallery.
   The mode and the selection are carried over, so navigating folders
   (which re-enters the page) does not snap the user out of the Archive
   tab and the 'Move' popover survives folder switches."
  [{:keys [ui/store ui/event-handler action] :as w}]
  (let [[_ location] action
        param (get-in location [:location/query-params :folder])
        uuid (when (seq param)
               (parse-uuid param))]
    ;; the navbar and sidebar need the session; loaded once, since a
    ;; re-query would flash the frame's placeholder on every folder
    ;; navigation:
    (when-not (query/available? @store app-frame/user-session-query)
      (event-handler {} [[:data/query app-frame/user-session-query]]))
    (if (and (seq param)
             (nil? uuid))
      (folder-missing! w)
      (do
        (swap! store
               update
               :gallery
               (fn [gallery]
                 (-> (or gallery {})
                     (assoc :folder uuid
                            :offset 0
                            :page-size (layout/calc-page-size (grid/initial-offsets))
                            :mode (or (:mode gallery) :active)
                            :selected (or (:selected gallery) #{}))
                     ;; the drag state survives: a folder that sprang
                     ;; open mid-drag re-enters the page while the drag
                     ;; is still in flight, and its drop zones need it:
                     (dissoc :context-menu :file-drag? :mobile-folders?))))
        (scroll-top! store)
        (load-gallery! w)))))

(defn set-scroll!
  "Stores the offsets of the grid, when they changed (the scroll event
   fires many times per row scrolled)."
  [{:keys [ui/store action]}]
  (let [[_ offsets] action]
    (when (not= offsets (get-in @store [:gallery :scroll]))
      (swap! store assoc-in [:gallery :scroll] offsets))))

(defn scrolled!
  "After a pause in scrolling: loads the page that covers the viewport,
   when the visible entries left the loaded window."
  [{:keys [ui/store action] :as w}]
  (let [[_ offsets] action
        result (gq/result @store)]
    (when-let [window (layout/next-window {:offset (:gallery/offset result)
                                           :page-size (:gallery/page-size result)}
                                          offsets)]
      (swap! store update :gallery merge window)
      (load-gallery! w))))

(defn set-mode!
  [{:keys [ui/store action] :as w}]
  (let [[_ mode] action]
    (swap! store
           update
           :gallery
           (fn [gallery]
             (-> gallery
                 (selection/clear)
                 (assoc :mode mode
                        :offset 0))))
    (scroll-top! store)
    (load-gallery! w)))

(defn open-folder!
  [{:keys [action] :as w}]
  (let [[_ uuid] action]
    (navigate! w (gallery-href uuid) false)))

(defn select-range!
  "Expands a shift-click on `eid` to the range between the anchor and
   `eid` via the server (the grid only holds one page) and applies it."
  [{:keys [ui/store] :as w} eid]
  (let [anchor (get-in @store [:gallery :range-anchor])]
    (-> (fetch-query! w (gq/eids-query @store [anchor eid]))
        (.then (fn [response]
                 (when (:success? response)
                   (swap! store update :gallery
                          selection/apply-range
                          eid
                          (get-in response [:result :gallery/eids])))))
        (.catch (fn [error]
                  (js/console.error "range selection failed:" error)
                  (show-error! store "The selection failed. Please try again."))))))

(defn select-all!
  [{:keys [ui/store] :as w}]
  (-> (fetch-query! w (gq/eids-query @store))
      (.then (fn [response]
               (when (:success? response)
                 (swap! store update :gallery
                        selection/select-all
                        (get-in response [:result :gallery/eids])))))
      (.catch (fn [error]
                (js/console.error "select all failed:" error)
                (show-error! store "The selection failed. Please try again.")))))

(defn card-click!
  "A click on an entry: shift-click extends the selection from the
   anchor to the entry (like in macOS Finder), cmd-click (macOS) or
   ctrl-click (Windows / Linux) toggles the selection, a plain click
   opens the entry modal."
  [{:keys [ui/store ui/event-handler action] :as w}]
  (let [[_ eid] action
        event (:replicant/js-event w)]
    (cond
      (selection/shift-range-click? (:gallery @store) (.-shiftKey event))
      (select-range! w eid)

      (or (.-metaKey event)
          (.-ctrlKey event))
      (swap! store update :gallery selection/toggle eid)

      :else
      (event-handler {} [[:ui/modal-open :modals/gallery-entry
                          {:story-media/eid eid
                           ;; the shared modal panel is as wide as
                           ;; `sm:max-w-5xl` by default, which leaves a
                           ;; lot of empty space next to the cards:
                           :max-width "sm:max-w-fit"}]]))))

(defn checkbox-click!
  [{:keys [ui/store action] :as w}]
  (let [[_ eid] action
        event (:replicant/js-event w)]
    (.preventDefault event)
    (.stopPropagation event)
    (if (selection/shift-range-click? (:gallery @store) (.-shiftKey event))
      (select-range! w eid)
      (swap! store update :gallery selection/toggle eid))))

(defn bulk-command!
  "Runs the command `kind` (archive, unarchive or delete) on the
   selected entries."
  [{:keys [ui/store ui/event-handler action]}]
  (let [[_ kind] action
        eids (get-in @store [:gallery :selected])]
    (when (seq eids)
      (event-handler {}
                     [[:data/command
                       {:command/kind kind
                        :command/data {:story-media/eids eids}}
                       {:on-success [[:gallery/clear-selection]
                                     [:gallery/query]]
                        :on-error [[:ui/show-error]]}]]))))

(defn set-drag-image!
  "Replaces the browser's default drag preview with a small pill that
   describes what is being dragged, e.g. 'Move 3 items'. The element has
   to be in the DOM when `setDragImage` snapshots it, so it is placed
   off-screen and removed right after the snapshot."
  [event label]
  (let [el (js/document.createElement "div")]
    (set! (.-textContent el) label)
    (set! (.-cssText (.-style el))
          (str "position:absolute;top:-1000px;left:-1000px;"
               "padding:6px 12px;border-radius:9999px;"
               "background-color:#1e293b;color:#fff;"
               "font-size:13px;font-weight:500;white-space:nowrap;"
               "box-shadow:0 4px 12px rgba(0,0,0,0.25);"))
    (js/document.body.appendChild el)
    (.setDragImage (.-dataTransfer event) el 12 18)
    (js/setTimeout (fn [] (.remove el)) 0)))

(defn start-drag!
  "Marks a gallery drag as active. Also registers window-level cleanup
   listeners, since `dragend` does not fire on the drag source when it
   got unmounted mid-drag (e.g. after a folder sprang open and the grid
   re-rendered)."
  [store payload]
  (swap! store assoc-in [:gallery :drag] payload)
  (letfn [(cleanup! [_e]
            (js/window.removeEventListener "dragend" cleanup!)
            (js/window.removeEventListener "drop" cleanup!)
            (swap! store assoc-in [:gallery :drag] nil))]
    (js/window.addEventListener "dragend" cleanup!)
    (js/window.addEventListener "drop" cleanup!)))

(defn drag-start-media!
  "Drags the entry `eid`, or the whole selection when the entry is
   selected."
  [{:keys [ui/store action] :as w}]
  (let [[_ eid] action
        event (:replicant/js-event w)
        selected (get-in @store [:gallery :selected])
        eids (if (contains? selected eid)
               (set selected)
               #{eid})]
    (set! (.-effectAllowed (.-dataTransfer event)) "move")
    (.setData (.-dataTransfer event)
              "text/plain"
              (pr-str {:story-media/eids eids}))
    (set-drag-image! event
                     (str "Move "
                          (count eids)
                          (if (= 1 (count eids))
                            " item"
                            " items")))
    (start-drag! store {:drag/kind :media
                        :story-media/eids eids
                        ;; the loaded entries of the drag, to show them
                        ;; right away when they are dropped into the
                        ;; folder that sprang open mid-drag:
                        :drag/entries (filterv (comp eids :story-media/eid)
                                               (vals (:gallery/entries (gq/result @store))))
                        :drag/source-folder (gq/current-folder @store)})))

(defn drag-start-folder!
  [{:keys [ui/store action] :as w}]
  (let [[_ uuid name current-folder] action
        event (:replicant/js-event w)]
    (set! (.-effectAllowed (.-dataTransfer event)) "move")
    (.setData (.-dataTransfer event)
              "text/plain"
              (pr-str {:folder/uuid uuid}))
    (set-drag-image! event (str "Move \"" name "\""))
    (start-drag! store {:drag/kind :folder
                        :folder/uuid uuid
                        :folder/name name
                        :drag/source-folder current-folder})))

(defn drag-end!
  [{:keys [ui/store]}]
  (swap! store assoc-in [:gallery :drag] nil))

(defn drag-enter!
  [{:keys [ui/store action] :as w}]
  (let [[_ uuid opts] action
        event (:replicant/js-event w)]
    (when (drag/valid-target? (get-in @store [:gallery :drag]) uuid opts)
      (.preventDefault event)
      (swap! store update-in [:gallery :drag]
             drag/set-over uuid (.-timeStamp event)))))

(defn drag-over!
  "Accepts the drop on a valid target. Hovering over a folder for the
   spring-load delay opens it (not for the current-folder drop zones)."
  [{:keys [ui/store ui/event-handler action] :as w}]
  (let [[_ uuid opts] action
        event (:replicant/js-event w)
        d (get-in @store [:gallery :drag])]
    (when (drag/valid-target? d uuid opts)
      (.preventDefault event)
      (set! (.-dropEffect (.-dataTransfer event)) "move")
      (when (and (not (:current-folder? opts))
                 (drag/spring-load-elapsed? d uuid (.-timeStamp event)))
        (swap! store update-in [:gallery :drag] drag/clear-over uuid)
        (event-handler {} [[:gallery/open-folder uuid]])))))

(defn drag-leave!
  "`dragleave` also fires when the drag moves onto a child of the
   target, so the highlight is only cleared when the drag really left."
  [{:keys [ui/store action] :as w}]
  (let [[_ uuid] action
        event (:replicant/js-event w)]
    (when-not (and (.-relatedTarget event)
                   (.contains (.-currentTarget event)
                              (.-relatedTarget event)))
      (swap! store update-in [:gallery :drag] drag/clear-over uuid))))

(defn read-drop-data
  [event]
  (try
    (edn/read-string (.getData (.-dataTransfer event) "text/plain"))
    (catch :default _e
      nil)))

(defn drop!
  "Dispatches a drop on a folder by its payload: either gallery entries
   or a dragged folder."
  [{:keys [ui/store ui/event-handler action] :as w}]
  (let [[_ uuid opts] action
        event (:replicant/js-event w)
        d (get-in @store [:gallery :drag])]
    (.preventDefault event)
    (swap! store update-in [:gallery :drag] drag/clear-over uuid)
    (when (drag/valid-target? d uuid opts)
      (let [data (read-drop-data event)]
        (cond
          (:story-media/eids data)
          (event-handler {} [[:gallery/story-media-move uuid (:story-media/eids data)]])

          (:folder/uuid data)
          (event-handler {} [[:gallery/folder-move uuid (:folder/uuid data)]])

          :else
          (js/console.warn "Ignored drop with unknown payload:"
                           (.getData (.-dataTransfer event) "text/plain")))))))

(defn mark-moving-out!
  "Fades entries or folders out after a move. The optimistic update
   (`ui.gallery.move`) removes them right away, the mark keeps them
   faded when a gallery response that was requested before the move
   brings them back. It clears itself after a few seconds: normally the
   refresh has removed them long before that, and if the move failed
   they fade back in."
  [store keys*]
  (swap! store update :gallery drag/mark-moving-out keys*)
  (js/setTimeout (fn []
                   (swap! store update :gallery drag/unmark-moving-out keys*))
                 drag/moving-out-ms))

(defn leaves-shown-folder?
  "Does a move to the folder `target-uuid` move the dragged entries out
   of the folder that is currently shown? Only then should they fade
   out: a drop into the shown folder (after a folder sprang open
   mid-drag) makes them appear in the grid."
  [state target-uuid]
  (not= target-uuid
        (gq/current-folder state)))

(defn folder-command
  [command after]
  [:data/command
   command
   {:on-success (into [[:gallery/query]] after)
    :on-error [[:ui/show-error]]}])

(defn story-media-move!
  "Moves the entries `eids` into the folder `target-uuid`. The grid
   shows the move right away (see `ui.gallery.move`): the entries leave
   the shown folder, or appear in it when the drag started in another
   folder (the dragged entries are known from the drag map, the 'Move
   here' button of the popover leaves it to the refresh)."
  [{:keys [ui/store ui/event-handler action]}]
  (let [[_ target-uuid eids] action
        state @store]
    (if (leaves-shown-folder? state target-uuid)
      (do
        (mark-moving-out! store eids)
        (swap! store gq/update-result move/remove-entries eids))
      (swap! store gq/update-result move/add-entries
             (get-in state [:gallery :drag :drag/entries])))
    (swap! store update :gallery selection/clear)
    (event-handler {}
                   [(folder-command {:command/kind :command/story-media-move
                                     :command/data {:uuid target-uuid
                                                    :story-media/eids (set eids)}}
                                    [])])))

(defn folder-move!
  [{:keys [ui/store ui/event-handler action]}]
  (let [[_ target-uuid folder-uuid] action]
    ;; Dropping a folder on itself is an easy slip while dragging and
    ;; simply does nothing.
    (when-not (= target-uuid folder-uuid)
      (let [state @store]
        (if (leaves-shown-folder? state target-uuid)
          (do
            (mark-moving-out! store [folder-uuid])
            (swap! store gq/update-result move/remove-folder folder-uuid))
          (swap! store gq/update-result move/add-folder folder-uuid
                 {:name (get-in state [:gallery :drag :folder/name])})))
      (event-handler {}
                     [(folder-command {:command/kind :command/folder-move
                                       :command/data {:folder/uuid folder-uuid
                                                      :target-folder/uuid target-uuid}}
                                      [])]))))

(defn move-here!
  "The 'Move here' button of the 'Move' popover: moves the selected
   entries into the folder the gallery shows."
  [{:keys [ui/store ui/event-handler]}]
  (let [eids (get-in @store [:gallery :selected])]
    (swap! store assoc-in [:gallery :move-popover?] false)
    (when (seq eids)
      (event-handler {}
                     [[:gallery/story-media-move (gq/current-folder @store) eids]]))))

(def context-menu-width
  160)

(defn context-menu!
  [{:keys [ui/store action] :as w}]
  (let [[_ uuid name] action
        event (:replicant/js-event w)
        x (.-clientX event)
        y (.-clientY event)]
    (.preventDefault event)
    (.stopPropagation event)
    (swap! store assoc-in [:gallery :context-menu]
           {:x (if (< js/window.innerWidth (+ x context-menu-width))
                 (- x context-menu-width)
                 x)
            :y y
            :uuid uuid
            :name name})))

(defn folder-new!
  [{:keys [ui/event-handler action]}]
  (let [[_ current-folder] action]
    (event-handler {}
                   [[:ui/modal-open :modals/gallery-prompt
                     {:title "Enter a folder name:"
                      :value ""
                      :on-submit [:gallery/folder-create current-folder]}]])))

(defn folder-create!
  [{:keys [ui/event-handler action]}]
  (let [[_ current-folder name] action]
    (event-handler {}
                   [(folder-command {:command/kind :command/folder-create
                                     :command/data {:name name
                                                    :current-folder current-folder}}
                                    [])])))

(defn folder-rename-ask!
  [{:keys [ui/event-handler action]}]
  (let [[_ uuid name] action]
    (event-handler {}
                   [[:ui/modal-open :modals/gallery-prompt
                     {:title "Rename the folder:"
                      :value name
                      :on-submit [:gallery/folder-rename uuid]}]])))

(defn folder-rename!
  [{:keys [ui/event-handler action]}]
  (let [[_ uuid name] action]
    (event-handler {}
                   [(folder-command {:command/kind :command/folder-rename
                                     :command/data {:uuid uuid
                                                    :name name}}
                                    [])])))

(defn prompt-submit!
  "Submits the prompt modal: closes it and dispatches its `:on-submit`
   action with the entered text appended."
  [{:keys [ui/store ui/event-handler]}]
  (let [state @store
        value (or (get-in state [:gallery :prompt-value]) "")
        on-submit (get-in state [:ui/modal :params :on-submit])]
    ;; the submit button is disabled for these values, this guards the
    ;; implicit submission via the Enter key:
    (when (and (seq value)
               (<= (count value) prompt-modal/max-name-length)
               on-submit)
      (event-handler {}
                     [[:ui/modal-close]
                      (conj on-submit value)]))))

(defn folder-delete-ask!
  "Fetches what the deletion would affect and opens the confirmation."
  [{:keys [ui/store ui/event-handler action] :as w}]
  (let [[_ uuid name] action
        open-confirm! (fn [preview]
                        (event-handler {}
                                       [[:ui/modal-open :modals/gallery-confirm
                                         {:title "Delete folder"
                                          :message (folder-delete/message name preview)
                                          :checkbox-label (folder-delete/checkbox-label preview)
                                          :confirm-label "Delete"
                                          :on-confirm [[:gallery/folder-delete uuid]]}]]))]
    (-> (fetch-query! w (gq/folder-delete-preview-query uuid))
        (.then (fn [response]
                 (open-confirm! (get-in response [:result :folder/delete-preview]))))
        (.catch (fn [error]
                  (js/console.error "folder delete preview failed:" error)
                  (open-confirm! nil))))
    (swap! store assoc-in [:gallery :context-menu] nil)))

(defn folder-delete!
  [{:keys [ui/store ui/event-handler action]}]
  (let [[_ uuid] action]
    (event-handler {}
                   [(folder-command {:command/kind :command/folder-delete
                                     :command/data {:uuid uuid
                                                    :delete-items? (boolean
                                                                     (get-in @store [:gallery :confirm-checkbox?]))}}
                                    [])])))

(def accepted-types
  #{"image/jpeg"
    "image/png"
    "video/mp4"
    "video/quicktime"
    "video/mov"})

(defn upload-files!
  "Hands the files to the quick upload, which validates them, uploads
   the valid ones and creates a story media per file. The gallery
   refreshes via the db-view notify loop when the story medias are
   transacted.

   The story medias land in the folder that is open when the upload
   starts (nil is the top-level gallery), also when the user navigates
   to another folder while the files are still uploading.

   The upload status card (`ui.upload-status`) tells the user which
   files were rejected and why, together with the `pre-rejected` ones
   (see `drop-files!`), and when the upload failed. The header shows
   the progress of the running uploads."
  ([w files]
   (upload-files! w files nil))
  ([{:keys [ui/store]} files pre-rejected]
   (let [files (vec (array-seq files))
         batch (when (or (seq files) (seq pre-rejected))
                 (upload-status/start! store (+ (count files) (count pre-rejected))))]
     (if (seq files)
       (story-upload/handle-quick-story-files!
        files
        {:story-media/folder (get-in @store [:gallery :folder])}
        {:on-checked (fn [{:keys [rejected]}]
                       (upload-status/checked! store batch (concat pre-rejected rejected)))
         :on-error (fn [{:keys [message]}]
                     (upload-status/failed! store batch message))})
       (when batch
         (upload-status/checked! store batch pre-rejected))))))

(defn drop-files!
  "Files dropped from the OS on the upload panel or the page overlay.
   Files of other types are not handed to the upload, but listed as
   rejected next to the ones the upload rejects."
  [{:keys [ui/store] :as w}]
  (let [event (:replicant/js-event w)
        items (.-items (.-dataTransfer event))
        files (keep (fn [i]
                      (.getAsFile (aget items i)))
                    (range (.-length items)))
        {:keys [accepted rejected]} (group-by (fn [file]
                                                (if (accepted-types (.-type file))
                                                  :accepted
                                                  :rejected))
                                              files)]
    (.preventDefault event)
    (swap! store assoc-in [:gallery :file-drag?] false)
    (upload-files! w
                   (to-array accepted)
                   (map (fn [file]
                          (story-upload/unsupported-type (.-name file)
                                                         (.-type file)))
                        rejected))))

(defn file-drag-enter!
  "Only files from the OS open the drop overlay, not gallery drags."
  [{:keys [ui/store] :as w}]
  (let [event (:replicant/js-event w)]
    (when (= "Files"
             (first (array-seq (.-types (.-dataTransfer event)))))
      (swap! store assoc-in [:gallery :file-drag?] true))))

(defonce upload-watch-installed?
  (atom false))

(defn gallery-page?
  [state]
  (= :pages/gallery
     (get-in state [:location :location/page-id])))

(defn watch-uploads!
  "Mirrors the running uploads of the upload manager into the gallery
   state, so the header can show their progress.

   Installed once per page load and kept for the lifetime of the SPA,
   like the upload driver and the notify loop of `ui.core`: the store
   and the upload manager are singletons, so there is nothing to
   release, and a page has no unload hook. It only touches the store
   while the gallery page is shown, so uploads that finish on another
   page do not trigger renders there."
  [{:keys [ui/store]}]
  (when (compare-and-set! upload-watch-installed? false true)
    (add-watch upload-manager/upload-state
               ::gallery-uploads
               (fn [_key _ref _old new]
                 (when (gallery-page? @store)
                   (let [uploads (into {}
                                       (remove (fn [[_id upload]]
                                                 (:hidden upload)))
                                       (:uploads new))]
                     (when (not= uploads (get-in @store [:gallery :uploads]))
                       (swap! store assoc-in [:gallery :uploads] uploads))))))))

(defn abort-upload!
  [{:keys [action]}]
  (let [[_ id] action]
    (when-let [abort (get-in @upload-manager/upload-state [:uploads id :abort])]
      (abort))))

(defn entry-query!
  "Loads the entry for the modal. Only one entry query stays in the log."
  [{:keys [ui/store ui/event-handler action]}]
  (let [[_ eid] action
        q (gq/entry-query eid)]
    (swap! store update :ui.query/log gq/prune-log gq/entry-query? q)
    (event-handler {} [[:data/query q]])))

(defn handler
  [kind f]
  {:ui.action/kind kind
   :ui.action/handler f})

(def handlers
  [(handler :gallery/init init!)
   (handler :gallery/query load-gallery!)
   (handler :gallery/set-scroll set-scroll!)
   (handler :gallery/scrolled scrolled!)
   (handler :gallery/scroll-top (fn [{:keys [ui/store]}]
                                  (scroll-top! store)))
   (handler :gallery/set-mode set-mode!)
   (handler :gallery/open-folder open-folder!)
   (handler :gallery/card-click card-click!)
   (handler :gallery/checkbox-click checkbox-click!)
   (handler :gallery/clear-selection (fn [{:keys [ui/store]}]
                                       (swap! store update :gallery selection/clear)))
   (handler :gallery/select-all select-all!)
   (handler :gallery/bulk-command bulk-command!)
   (handler :gallery/drag-start-media drag-start-media!)
   (handler :gallery/drag-start-folder drag-start-folder!)
   (handler :gallery/drag-end drag-end!)
   (handler :gallery/drag-enter drag-enter!)
   (handler :gallery/drag-over drag-over!)
   (handler :gallery/drag-leave drag-leave!)
   (handler :gallery/drop drop!)
   (handler :gallery/story-media-move story-media-move!)
   (handler :gallery/folder-move folder-move!)
   (handler :gallery/move-here move-here!)
   (handler :gallery/context-menu context-menu!)
   (handler :gallery/close-context-menu (fn [{:keys [ui/store]}]
                                          (swap! store assoc-in [:gallery :context-menu] nil)))
   (handler :gallery/folder-new folder-new!)
   (handler :gallery/folder-create folder-create!)
   (handler :gallery/folder-rename-ask folder-rename-ask!)
   (handler :gallery/folder-rename folder-rename!)
   (handler :gallery/prompt-submit prompt-submit!)
   (handler :gallery/folder-delete-ask folder-delete-ask!)
   (handler :gallery/folder-delete folder-delete!)
   (handler :gallery/upload-files (fn [{:keys [action] :as w}]
                                    (upload-files! w (second action))))
   (handler :gallery/drop-files drop-files!)
   (handler :gallery/file-drag-enter file-drag-enter!)
   (handler :gallery/file-drag-leave (fn [{:keys [ui/store]}]
                                       (swap! store assoc-in [:gallery :file-drag?] false)))
   (handler :gallery/watch-uploads watch-uploads!)
   (handler :gallery/abort-upload abort-upload!)
   (handler :gallery/entry-query entry-query!)])
